import { Action, combineReducers, configureStore } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { allProjectsExportReducer } from './modules/allProjectsExport'
import { promoCodeModifyReducer } from './modules/promoCodeModify'
import { promoCodeSendReducer } from './modules/promoCodeSend'
import { validatePromoCodeReducer } from './modules/promoCodeValidate'
import { promoCodesReducer } from './modules/promoCodes'
import { reportingReducer } from './modules/reporting'
import { unsubscribeReducer } from './modules/unsubscribe'
import {
  activityByProjectReducer,
  activityListReducer,
  activityMarkAsReadReducer,
  activityMarkMessagesAsReadReducer,
  activitySendMessageReducer,
} from './reducers/activityReducer'
import {
  addressCreateReducer,
  addressDeleteReducer,
  addressListReducer,
  addressUpdateReducer,
} from './reducers/addressReducers'
import { documentReducer } from './reducers/documentReducers'
import {
  emailInviteReducer,
  emailPasswordReducer,
  emailProjectNotificationReducer,
  emailSupportReducer,
  emailValidationReducer,
} from './reducers/emailReducers'
import {
  fileDownloadAllReducer,
  fileDownloadReducer,
} from './reducers/fileReducers'
import {
  machineCreateReducer,
  machineDeleteReducer,
  machineListReducer,
  machineUpdateReducer,
} from './reducers/machineReducers'
import {
  loginTabReducer,
  sideNavReducer,
  signupTabReducer,
} from './reducers/navigationReducer'
import {
  partCreateReducer,
  partDeleteReducer,
  partDetailsReducer,
  partListReducer,
  partUpdateReducer,
} from './reducers/partReducers'
import {
  programCreateReducer,
  programDeleteReducer,
  programListReducer,
  programUpdateReducer,
} from './reducers/programReducers'
import {
  allProjectListReducer,
  projectAEAddPODocsReducer,
  projectAddContributorsReducer,
  projectAddPODocsReducer,
  projectArchiveReducer,
  projectCreateReducer,
  projectDeleteReducer,
  projectDeliverablesReducer,
  projectDetailsReducer,
  projectDocumentSignedReducer,
  projectExitReducer,
  projectFileDeleteReducer,
  projectFilesReducer,
  projectListReducer,
  projectMarkAsPaidReducer,
  projectRemoveContributorsReducer,
  projectRemoveDeliverableReducer,
  projectRequestsDetailsReducer,
  projectStepReducer,
  projectSubmitQuoteReducer,
  projectUpdateQuoteReducer,
  projectUpdateReducer,
} from './reducers/projectReducers'
import {
  userActivateReducer,
  userAdminCompleteSignUpReducer,
  userAdminListReducer,
  userConfirmEmailReducer,
  userDeactivateReducer,
  userDeleteReducer,
  userDetailsReducer,
  userGeneratePasswordResetLinkReducer,
  userInviteReducer,
  userListReducer,
  userLoginReducer,
  userRegisterReducer,
  userResetPasswordReducer,
  userUpdateAdminInfoReducer,
  userUpdateInfoReducer,
  userUpdatePasswordReducer,
} from './reducers/userReducers'

const reducer = combineReducers({
  allProjectsExport: allProjectsExportReducer,
  machineCreate: machineCreateReducer,
  machineList: machineListReducer,
  machineUpdate: machineUpdateReducer,
  machineDelete: machineDeleteReducer,
  partCreate: partCreateReducer,
  partList: partListReducer,
  partDetails: partDetailsReducer,
  partUpdate: partUpdateReducer,
  partDelete: partDeleteReducer,
  projectCreate: projectCreateReducer,
  projectDetails: projectDetailsReducer,
  projectRequestsDetails: projectRequestsDetailsReducer,
  projectUpdate: projectUpdateReducer,
  projectSubmitQuote: projectSubmitQuoteReducer,
  projectUpdateQuote: projectUpdateQuoteReducer,
  projectAddPODocs: projectAddPODocsReducer,
  projectAEAddPODocs: projectAEAddPODocsReducer,
  projectAddContributors: projectAddContributorsReducer,
  projectRemoveContributors: projectRemoveContributorsReducer,
  projectList: projectListReducer,
  projectStep: projectStepReducer,
  projectExit: projectExitReducer,
  projectDelete: projectDeleteReducer,
  projectFiles: projectFilesReducer,
  projectFileDelete: projectFileDeleteReducer,
  projectMarkAsPaid: projectMarkAsPaidReducer,
  projectDocumentSigned: projectDocumentSignedReducer,
  projectDeliverables: projectDeliverablesReducer,
  projectRemoveDeliverable: projectRemoveDeliverableReducer,
  projectArchive: projectArchiveReducer,
  promoCodes: promoCodesReducer,
  promoCodeCreate: promoCodeModifyReducer,
  promoCodeSend: promoCodeSendReducer,
  allProjectList: allProjectListReducer,
  programCreate: programCreateReducer,
  programList: programListReducer,
  programUpdate: programUpdateReducer,
  programDelete: programDeleteReducer,
  userLogin: userLoginReducer,
  userRegister: userRegisterReducer,
  userDetails: userDetailsReducer,
  userUpdateInfo: userUpdateInfoReducer,
  userUpdateAdminInfo: userUpdateAdminInfoReducer,
  userAdminCompleteSignUp: userAdminCompleteSignUpReducer,
  userDeactivate: userDeactivateReducer,
  userActivate: userActivateReducer,
  userDelete: userDeleteReducer,
  userList: userListReducer,
  userAdminList: userAdminListReducer,
  userConfirmEmail: userConfirmEmailReducer,
  userUpdatePassword: userUpdatePasswordReducer,
  userResetPassword: userResetPasswordReducer,
  userGeneratePasswordResetLink: userGeneratePasswordResetLinkReducer,
  userInvite: userInviteReducer,
  emailValidation: emailValidationReducer,
  emailPassword: emailPasswordReducer,
  emailInvite: emailInviteReducer,
  emailProjectNotification: emailProjectNotificationReducer,
  emailSupport: emailSupportReducer,
  addressCreate: addressCreateReducer,
  addressList: addressListReducer,
  addressDelete: addressDeleteReducer,
  addressUpdate: addressUpdateReducer,
  activityList: activityListReducer,
  activityByProject: activityByProjectReducer,
  activitySendMessage: activitySendMessageReducer,
  activityMarkAsRead: activityMarkAsReadReducer,
  activityMarkMessagesAsRead: activityMarkMessagesAsReadReducer,
  document: documentReducer,
  sideNav: sideNavReducer,
  loginTab: loginTabReducer,
  signupTab: signupTabReducer,
  fileDownload: fileDownloadReducer,
  fileDownloadAll: fileDownloadAllReducer,
  reporting: reportingReducer,
  unsubscribe: unsubscribeReducer,
  validatePromoCode: validatePromoCodeReducer,
})

export const store = configureStore({ reducer })

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export interface ActionWithPayload<T> extends Action {
  payload: T
}

export default store
